import React from 'react';
import { styled } from '@mui/material/styles';
// import clsx from 'clsx';
import { MenuAction } from 'models/types';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import UserNavbarHeader from 'components/UserNavbarHeader';
import SmeAppBar from 'containers/SmeAppBar';
import { drawerWidth } from 'kosmosConfig';
import SidebarItem from 'components/SidebarItem';
import Footer from 'kosmos/components/Footer';
import MenuConfigContext from 'data/MenuConfigContext';
import { IMenuItem } from 'data/MenuConfig';
import Logo from '../../logos/Logo';
import CurrentUser from 'auth/currentUser';
import { EFeature } from 'auth/Authentication';

const PREFIX = 'TfpApp';

const classes = {
  root: `${PREFIX}-root`,
  toolbar: `${PREFIX}-toolbar`,
  appBar: `${PREFIX}-appBar`,
  appBarShift: `${PREFIX}-appBarShift`,
  menuButton: `${PREFIX}-menuButton`,
  menuButtonHidden: `${PREFIX}-menuButtonHidden`,
  title: `${PREFIX}-title`,
  appBarSpacer: `${PREFIX}-appBarSpacer`,
  dividerSpacer: `${PREFIX}-dividerSpacer`,
  contentContainer: `${PREFIX}-contentContainer`,
  content: `${PREFIX}-content`,
  container: `${PREFIX}-container`,
  paper: `${PREFIX}-paper`,
  fixedHeight: `${PREFIX}-fixedHeight`,
  drawerPaper: `${PREFIX}-drawerPaper`,
  fixedSidebar: `${PREFIX}-fixedSidebar`,
  toolbarIcon: `${PREFIX}-toolbarIcon`,
  logo: `${PREFIX}-logo`,
  sidebarHeader: `${PREFIX}-sidebarHeader`,
  sidebarBottomSpacer: `${PREFIX}-sidebarBottomSpacer`,
  list: `${PREFIX}-list`,
};

const Root = styled('div')((
  {
    theme
  }
) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
  },

  [`& .${classes.toolbar}`]: {
    background: 'white',
    color: '#121212',
    paddingRight: 24, // keep right padding when drawer closed
  },

  [`& .${classes.appBar}`]: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  [`& .${classes.appBarShift}`]: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  [`& .${classes.menuButton}`]: {
    marginRight: 36,
  },

  [`& .${classes.menuButtonHidden}`]: {
    display: 'none',
  },

  [`& .${classes.title}`]: {
    flexGrow: 1,
  },

  [`& .${classes.appBarSpacer}`]: theme.mixins.toolbar,
  [`& .${classes.dividerSpacer}`]: {
    height: 45,
    backgroundColor: theme.palette.primary.main,
  },

  [`& .${classes.contentContainer}`]: {
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },

  [`& .${classes.content}`]: {
    flex: 1,
    display: 'flex',
  },

  [`& .${classes.container}`]: {
    position: 'relative',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },

  [`& .${classes.paper}`]: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
  },

  [`& .${classes.fixedHeight}`]: {
    height: 240,
  },

  [`& .${classes.drawerPaper}`]: {
    backgroundColor: '#121212',
    border: 'none',
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
  },
  [`& .${classes.fixedSidebar}`]: {
    backgroundColor: '#121212',
    border: 'none',
    position: 'fixed',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  [`& .${classes.toolbarIcon}`]: {
    textAlign: 'center',
    padding: '8px',
    color: 'white',
    backgroundColor: 'transparent',
    ...theme.mixins.toolbar
  },
  [`& .${classes.logo}`]: {
    margin: '0 auto'
  },
  [`& .${classes.sidebarHeader}`]: {
    zIndex: 1000,
    width: drawerWidth,
    position: 'relative',
    background: 'radial-gradient(circle, rgba(68,140,203,1) 0%, rgba(38,50,105,1) 100%)',
  },
  [`& .${classes.sidebarBottomSpacer}`]: {
    backgroundColor: '#121212',
    position: 'absolute',
    bottom: 0,
    height: 34,
    width: '100%',
  },
  [`& .${classes.list}`]: {
    flex: 1,
    color: 'white',
    overflowY: 'scroll',
  },
}));

interface Props {
  actions?: MenuAction[];
  children: React.ReactNode;
}

export const tfpDefaultMenuList = (): IMenuItem[] => {
  const currentUser = CurrentUser.getInstance();
  const orgId = currentUser.getOrganisationId();
  const canViewOrganisation = currentUser.canManageResource(EFeature.organisation);
  let menus = [];

  if (canViewOrganisation && orgId) {
    menus.push({
      label: 'My Organisation',
      path: `/organisations/${orgId}`,
      menuId: 'my-org'
    });
  }
  if (currentUser.canManageResource(EFeature.companyList)) {
    menus = [
      ...menus,
      {
        label: 'My Company Lists',
        path: '/companyLists',
        menuId: 'company-lists',
      },
    ];
  }
  if (currentUser.canManageResource(EFeature.scoreCompany)) {
    menus = [
      ...menus,
      {
        label: 'My Scored Companies',
        path: '/scored_companies',
        menuId: 'scored-companies',
      },
      {
        label: 'Search Companies',
        path: '/home',
        menuId: 'home',
      },
    ];
  }

  if (currentUser.canManageResource(EFeature.smartGovernance)) {
    menus.push(
      {
        label: 'My Smart Governances',
        path: '/smart_governances',
        menuId: 'smart_governances',
      },
    );
  }

  menus.push(
    {
      label: 'My Company Registrations',
      path: '/wome_applications',
      menuId: 'wome_applications',
    },
  );

  menus.push(
    {
      label: 'My Scored Transactions',
      path: '/transactions',
      menuId: 'transactions',
    },
  );
  return menus;
};

export const reportSectionMenus: IMenuItem[] = [
  {
    label: 'ESG Ratings',
    path: '#esg-ratings',
    icon: 'SustainabilitySvg',
    menuId: 'esg-ratings',
  },
  {
    label: 'Sustainable Development Goals',
    path: '#sustainable-development-goals',
    menuId: 'sustainable-development-goals',
  },
  {
    label: 'Dual Use Goods',
    path: '#dual-use-goods-check',
    menuId: 'dual-use-goods-check',
  },
  {
    label: 'Dual Use Services',
    path: '#dual-use-services-check',
    menuId: 'dual-use-services-check',
  },
  {
    label: 'Illegal Logging',
    path: '#illegal-logging-check',
    menuId: 'illegal-logging-check',
  },
  {
    label: 'EU Taxonomy Score',
    path: '#eu-taxonomy-score',
    menuId: 'eu-taxonomy-score',
  },
  {
    label: 'EU Taxonomy Regulation DNSH',
    path: '#eu-taxonomy-regulation-dhsh',
    menuId: 'eu-taxonomy-regulation-dhsh',
  },
  {
    label: 'SFDR',
    path: '#sustainable-finance-disclosure-regulation',
    menuId: 'sustainable-finance-disclosure-regulation',
  },
  {
    label: 'Greenhouse Gases',
    path: '#greenhouse-gases',
    menuId: 'greenhouse-gases',
  },
  {
    label: 'Adverse News',
    path: '#news-check',
    menuId: 'newc-check',
  },
  {
    label: 'Governance',
    path: '#governance',
    menuId: 'governance',
  },
  {
    label: 'Appendix',
    icon: 'CompanySvg',
    path: '#appendix',
    menuId: 'appendix',
  },
  {
    label: 'UNSDG Breakdown',
    path: '#unsdg-breakdown-appendix',
    menuId: 'unsdg-breakdown-appendix',
  },
  {
    label: 'GHG Scope 3, Supply Chains',
    path: '#ghg-appendix',
    menuId: 'ghg-appendix',
  },
];

export default function TfpApp(props: Props): JSX.Element {
  const [menuItems, setMenuItems] = React.useState<IMenuItem[]>(tfpDefaultMenuList());
  const { actions } = props;

  return (
    <Root className={classes.root}>
      <CssBaseline />
      <SmeAppBar
        hideNotification={true}
        open={true}
        title="Sustainability ratings"
        actions={actions}
      />
      <Drawer
        variant="permanent"
        classes={{
          paper: classes.drawerPaper
        }}
        open={true}
      >
        <div className={classes.fixedSidebar}>
          <div className={classes.sidebarHeader}>
            <div className={classes.toolbarIcon}>
              <Logo className={classes.logo} height={75} color="white" alt="coriolisLogoWhite" />
            </div>
            <UserNavbarHeader
              open={true}
              transparent={true}
            />
            <div className={classes.sidebarBottomSpacer} />
          </div>
          <List dense={true} className={classes.list}>
            {(menuItems).map((menuItem) => (
              <SidebarItem
                key={menuItem.label}
                label={menuItem.label}
                icon={menuItem.icon}
                path={menuItem.path}
                disabled={menuItem.disabled}
                minimise={false}
              />
            ))}
          </List>
          <Footer />
        </div>
      </Drawer>

      <main className={classes.contentContainer}>
        <div className={classes.appBarSpacer} />
        <div className={classes.dividerSpacer} />
        <div className={classes.content}>
          <MenuConfigContext.Provider value={{
            menuItems,
            setMenuItems
          }}>
            {props.children}
          </MenuConfigContext.Provider>
        </div>
      </main>
    </Root>
  );
}
